import { ChangeEvent, FC, useCallback, useMemo } from "react"
import type { JSONSchema7 } from "json-schema"
import { NumberInput, TextInput, MuiDateInput } from "resha"
import { ReactComponent as ArrowIcon } from "icons/lib/arrow-down.svg"
import { MultipleChoice, Checkbox, Select, MenuItem } from "dash-styled"

const Icon: React.ElementType = ({ className, ...props }) => (
    <ArrowIcon className={`w-6 h-6 mr-2 p-1 ${className}`} {...props} />
)

interface ContentInputProps {
    schema: JSONSchema7
    value: undefined | string | number | boolean | string[]
    onChange: (data: any) => void
    readOnly: boolean
    className: string
    placeholder: string
}

export const ContentInput: FC<ContentInputProps> = ({
    schema,
    value,
    readOnly,
    className,
    onChange,
    placeholder
}) => {
    const choices = useMemo<string[]>(() => {
        const items =
            schema.type === "array"
                ? (schema.items as JSONSchema7)?.enum
                : schema.enum
        return items ? (items as string[]) : []
    }, [schema])
    const selectedIndices = useMemo<number[]>(
        () =>
            Array.isArray(value)
                ? value.flatMap(item => {
                      const index = choices?.findIndex(c => c === item)

                      return index === -1 ? [] : [index]
                  })
                : [],
        [choices, value]
    )

    const handleMultipleSelectChange = useCallback(
        (index: number) => {
            const item = choices[index]

            if (item === undefined) {
                return
            }
            onChange(
                Array.isArray(value)
                    ? value.includes(item)
                        ? value.filter(i => i !== item)
                        : value.concat([item])
                    : []
            )
        },
        [choices, onChange, value]
    )
    const handleSelectChange = (
        event: ChangeEvent<{
            name?: string
            value: unknown
        }>
    ) => {
        onChange(event.target.value)
    }
    const handleCheckboxChange = useCallback(
        () => onChange(!value),
        [onChange, value]
    )

    if (schema.type === "integer" || schema.type === "number") {
        return (
            <NumberInput
                transparentBg
                syncValue
                readOnly={readOnly}
                placeholder={placeholder}
                size="xs"
                value={value !== undefined ? String(value) : ""}
                minimum={schema.minimum ?? schema.exclusiveMinimum}
                maximum={schema.maximum ?? schema.exclusiveMaximum}
                type={schema.type === "integer" ? "integer" : "float"}
                onValueChange={onChange}
                inputClassName={`p-0 text-sm w-full overflow-hidden leading-tight min-h-4`}
                className={`flex w-full p-3 border-2 rounded ${className}`}
            />
        )
    }

    if (choices.length !== 0) {
        return schema.type === "array" ? (
            <div className={`flex w-full p-3 border-2 rounded ${className}`}>
                <MultipleChoice
                    inlineItems
                    withPanel={false}
                    variant="chip"
                    choices={choices}
                    selectedIndices={selectedIndices}
                    interactive={!readOnly}
                    hideUnchecked={readOnly}
                    onClick={handleMultipleSelectChange}
                    className={
                        !readOnly
                            ? "inline-flex flex-grow flex-row flex-wrap w-auto"
                            : "min-h-6 mb-0"
                    }
                    checkboxClassName={{
                        chip: `p-0 px-2 py-1 w-full text-sm break-all whitespace-pre-wrap leading-tight`
                    }}
                />
            </div>
        ) : (
            <Select
                fullWidth
                disableUnderline
                displayEmpty
                className="font-averta"
                classes={{
                    root: `flex w-full p-3 text-sm border-2 leading-tight rounded ${className}`
                }}
                IconComponent={Icon}
                value={choices.find(c => c === value) || ""}
                disabled={readOnly}
                onChange={handleSelectChange}
                renderValue={value =>
                    (value as string)?.length
                        ? Array.isArray(value)
                            ? value.join(", ")
                            : (value as string)
                        : "Choose the value"
                }
            >
                {choices.map(choice => (
                    <MenuItem
                        key={choice}
                        value={choice}
                        className="font-averta"
                    >
                        {choice}
                    </MenuItem>
                ))}
            </Select>
        )
    }
    if (schema.type === "boolean") {
        return (
            <div className={`flex w-full p-3 border-2 rounded ${className}`}>
                <Checkbox
                    variant="modern"
                    interactive={!readOnly}
                    isChecked={typeof value === "boolean" ? value : false}
                    onChange={handleCheckboxChange}
                    className="inline-flex flex-grow my-0"
                />
            </div>
        )
    }
    if (schema.type === "string") {
        if (schema.format === "date") {
            return (
                <MuiDateInput
                    size="small"
                    readOnly={readOnly}
                    placeholder={placeholder}
                    value={value as string}
                    onDateChange={onChange}
                    inputClassName="w-full text-sm pt-0 pb-0 pl-0 pr-0"
                    className={`flex w-full p-3 border-2 rounded ${className}`}
                />
            )
        }
        return (
            <TextInput
                singleLine
                enableCopyTextButton={false}
                enableMarkdown={false}
                minRows={1}
                readOnly={readOnly}
                placeholder={placeholder}
                value={value as string | undefined}
                onValueChange={onChange}
                className={`flex w-full p-3 border-2 rounded ${className}`}
                inputClassName={`p-0 text-sm w-full overflow-hidden leading-tight min-h-4`}
            />
        )
    }
    if (schema.type === "object") {
        if (value === undefined) {
            return null
        }
    }
    return <>Unsupported type</>
}
